import { supabase } from '../lib/supabase';
import { createNotification, getSentDedupeKeys } from './notificationService';
import { isNotificationTypeEnabled } from './notificationPreferences';

// Alertas de uso do tempo, verificados no navegador (como os de prazo, em
// deadlineReminderService.ts): não há rotina agendada no banco.
//
// - Cronômetro esquecido: avisa a própria pessoa quando o timer dela passa de
//   RUNNING_TIMER_LIMIT_HOURS ligado.
// - Projeto estourando: avisa cada administrador quando as horas lançadas de um
//   projeto ativo passam de 80% e de 100% do previsto (projects.planned_hours).

export const RUNNING_TIMER_LIMIT_HOURS = 8;
export const BUDGET_THRESHOLDS = [80, 100] as const;

// ---------------------------------------------------------------------------
// Cronômetro esquecido
// ---------------------------------------------------------------------------

export function runningTimerDedupeKey(entryId: string): string {
  return `timer:${entryId}`;
}

export function hoursRunning(startTime: string, now: Date): number {
  return (now.getTime() - new Date(startTime).getTime()) / (60 * 60 * 1000);
}

export function shouldWarnRunningTimer(
  entry: { id: string; start_time: string },
  now: Date,
  alreadySent: Set<string>
): boolean {
  if (hoursRunning(entry.start_time, now) < RUNNING_TIMER_LIMIT_HOURS) return false;
  return !alreadySent.has(runningTimerDedupeKey(entry.id));
}

function formatarInicio(startTime: string): string {
  return new Date(startTime).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const timerRodando = new Set<string>();

export async function checkRunningTimer(organizationId: string, userId: string): Promise<void> {
  const chave = `${organizationId}:${userId}`;
  if (timerRodando.has(chave)) return;
  timerRodando.add(chave);

  try {
    const { data: entrada, error } = await supabase
      .from('time_entries')
      .select('id, start_time, description')
      .eq('user_id', userId)
      .is('end_time', null)
      .order('start_time', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const timer = entrada as { id: string; start_time: string; description?: string | null } | null;
    if (!timer?.start_time) return;

    const agora = new Date();
    if (hoursRunning(timer.start_time, agora) < RUNNING_TIMER_LIMIT_HOURS) return;
    if (!(await isNotificationTypeEnabled(organizationId, userId, 'timer_running_long'))) return;

    const jaEnviadas = await getSentDedupeKeys(organizationId, userId, ['timer_running_long'], 30);
    if (!shouldWarnRunningTimer(timer, agora, jaEnviadas)) return;

    const horas = Math.floor(hoursRunning(timer.start_time, agora));
    const sobre = timer.description ? ` ("${timer.description}")` : '';
    await createNotification({
      organization_id: organizationId,
      user_id: userId,
      type: 'timer_running_long',
      title: 'Cronômetro ligado há muito tempo',
      content: `Seu cronômetro${sobre} está ligado há ${horas}h, desde ${formatarInicio(timer.start_time)}. Esqueceu de parar?`,
      metadata: { timeEntryId: timer.id, dedupeKeys: [runningTimerDedupeKey(timer.id)] },
    });
  } catch (err) {
    console.warn('Falha ao verificar cronômetro ligado:', err);
  } finally {
    timerRodando.delete(chave);
  }
}

// ---------------------------------------------------------------------------
// Projeto estourando as horas previstas
// ---------------------------------------------------------------------------

export interface ProjectBudget {
  id: string;
  name: string;
  plannedHours: number;
  executedHours: number;
}

export interface PlannedBudgetAlert {
  project: ProjectBudget;
  threshold: number; // maior faixa atingida que ainda não foi avisada
  percent: number;
  dedupeKeys: string[];
}

export function budgetDedupeKey(projectId: string, threshold: number, plannedHours: number): string {
  // O previsto entra na chave: se o projeto for replanejado, pode avisar de novo.
  return `budget:${projectId}:${threshold}:${plannedHours}`;
}

export function planBudgetAlerts(projects: ProjectBudget[], alreadySent: Set<string>): PlannedBudgetAlert[] {
  const alertas: PlannedBudgetAlert[] = [];
  for (const project of projects) {
    if (!(project.plannedHours > 0)) continue;
    const percent = (project.executedHours / project.plannedHours) * 100;
    const atingidas = BUDGET_THRESHOLDS.filter(t => percent >= t);
    if (atingidas.length === 0) continue;

    const novas = atingidas.filter(t => !alreadySent.has(budgetDedupeKey(project.id, t, project.plannedHours)));
    if (novas.length === 0) continue;

    // Passou de 80% e de 100% desde a última checagem: um aviso só, o mais
    // grave, que também quita a faixa menor.
    alertas.push({
      project,
      threshold: Math.max(...novas),
      percent,
      dedupeKeys: novas.map(t => budgetDedupeKey(project.id, t, project.plannedHours)),
    });
  }
  return alertas;
}

// Paginado: o PostgREST corta em 1000 linhas, e projetos com histórico teriam
// as horas executadas truncadas.
const PAGE_SIZE = 1000;

export async function fetchProjectTimeEntries(projectIds: string[]) {
  const entries: { project_id: string; duration: number | null }[] = [];
  if (projectIds.length === 0) return entries;

  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('time_entries')
      .select('project_id, duration')
      .in('project_id', projectIds)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    entries.push(...((data || []) as { project_id: string; duration: number | null }[]));
    if (!data || data.length < PAGE_SIZE) return entries;
  }
}

function hojeLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const orcamentoRodando = new Set<string>();

// Só para administradores (quem chama decide). Uma vez por dia por pessoa.
export async function checkProjectBudgets(organizationId: string, userId: string): Promise<void> {
  const chave = `project_budget_alerts_${organizationId}_${userId}`;
  const hoje = hojeLocal();
  try {
    if (localStorage.getItem(chave) === hoje) return;
  } catch {
    // segue sem o atalho
  }
  if (orcamentoRodando.has(chave)) return;
  orcamentoRodando.add(chave);

  try {
    // Desligado nas preferências: não gera, e não marca o dia.
    if (!(await isNotificationTypeEnabled(organizationId, userId, 'project_hours_budget'))) return;

    const { data: projetos, error } = await supabase
      .from('projects')
      .select('id, name, planned_hours')
      .eq('organization_id', organizationId)
      .eq('status', 'active')
      .gt('planned_hours', 0);
    if (error) throw error;

    const lista = (projetos ?? []) as Array<{ id: string | number; name: string; planned_hours: number | null }>;
    if (lista.length > 0) {
      const ids = lista.map(p => String(p.id));
      const lancamentos = await fetchProjectTimeEntries(ids);
      const minutos = new Map<string, number>();
      for (const l of lancamentos) {
        minutos.set(String(l.project_id), (minutos.get(String(l.project_id)) ?? 0) + (l.duration || 0));
      }

      const orcamentos: ProjectBudget[] = lista.map(p => ({
        id: String(p.id),
        name: p.name,
        plannedHours: Number(p.planned_hours) || 0,
        executedHours: (minutos.get(String(p.id)) ?? 0) / 60,
      }));

      const jaEnviadas = await getSentDedupeKeys(organizationId, userId, ['project_hours_budget'], 365);
      for (const alerta of planBudgetAlerts(orcamentos, jaEnviadas)) {
        const { project, threshold, percent } = alerta;
        const executadas = Math.round(project.executedHours);
        await createNotification({
          organization_id: organizationId,
          user_id: userId,
          type: 'project_hours_budget',
          title: threshold >= 100
            ? `Projeto passou das horas previstas`
            : `Projeto chegou a ${threshold}% das horas previstas`,
          content: `"${project.name}" está com ${executadas}h lançadas de ${project.plannedHours}h previstas (${Math.round(percent)}%).`,
          metadata: { projectId: project.id, threshold, dedupeKeys: alerta.dedupeKeys },
        });
      }
    }

    try {
      localStorage.setItem(chave, hoje);
    } catch {
      // sem storage, a próxima abertura confere de novo
    }
  } catch (err) {
    console.warn('Falha ao verificar horas previstas dos projetos:', err);
  } finally {
    orcamentoRodando.delete(chave);
  }
}
